<template>
  <VSearchableTile
    data-testid="drill-data-tile"
    class="drill-data-tile"
    :class="[tileClasses, { 'disabled-error': props.data.hole_data_status !== 'ready' }]"
    :selected="selected"
    :disabled="isAddedToModel"
    @click="handleClick(data)"
  >
    <template #default>
      <div class="drill-data-tile__label-and-status">
        <p class="drill-data-tile__title">{{ data.name }}</p>
        <p class="drill-data-tile__description">{{ assets }}</p>
        <p class="drill-data-tile__date-uploaded pt-1.5">{{ formatDateOnlyMed(data.created_at) }}</p>
        <div v-if="isAddedToModel" class="status">
          <CheckCircleIcon class="mr-1 inline-block w-4 h-4" />
          <span>Data set added to model</span>
        </div>
        <div v-else-if="data.hole_data_status !== 'ready'" class="status">
          <span v-if="data.hole_data_status === 'error'" class="text-red-800 text-sm align-middle">
            <ExclamationTriangleIcon class="inline-block mr-1 w-4" /> Data processing failed. Re-upload data in Data Files.
          </span>
          <span v-else class="flex items-center gap-2">
            <AnimatedLoadingSpinner class="w-4" />
            Data processing in progress</span
          >
        </div>
      </div>

      <div class="flex-shrink-0">
        <template v-if="data.view_access_type === 'public'">
          <label class="drill-data-tile__access-type bg-copper-800" type="badge" data-testid="drill-data-access-public">
            Public Info
          </label>
        </template>
        <template v-else>
          <label class="drill-data-tile__access-type bg-gold-900" type="badge" data-testid="drill-data-access-private">
            Confidential
          </label>
        </template>
      </div>
    </template>
  </VSearchableTile>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'
import { computed } from 'vue'
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/solid'
import AnimatedLoadingSpinner from '@/components/icons/AnimatedLoadingSpinner.svg'

import type { Drill } from '@/entities/drill'
import { formatDateOnlyMed } from '@/lib/datetime-format-utils'

const props = defineProps({
  data: {
    type: Object as PropType<Drill>,
    required: true,
  },
  selected: {
    type: Boolean,
    default: false,
  },
  isAddedToModel: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['selected'])

const assets = computed(() => props.data.assets.map(({ name }) => name).join(', '))
const tileClasses = computed(() => (props.isAddedToModel || props.data.hole_data_status !== 'ready' ? 'disabled' : ''))

function handleClick(data: Drill) {
  if (props.data.hole_data_status === 'ready' && !props.isAddedToModel) {
    emit('selected', data)
  }
}
</script>

<style lang="stylus" scoped>
.drill-data-tile
  @apply flex justify-between

  &.disabled-error
    @apply cursor-default

    & > *:not(.drill-data-tile__label-and-status, .status), .drill-data-tile__label-and-status > *:not(.status)
      @apply bg-gray-100 opacity-45

  p
    @apply m-0

  &__title
    @apply text-sm leading-5 font-medium text-gray-800

  &__description
    @apply text-xs leading-4 font-normal text-gray-700

  &__date-uploaded
    @apply text-xs leading-4 font-normal text-gray-700

  &__access-type
    @apply text-xs leading-3 font-bold text-white rounded-full px-4 py-1 mb-0 hidden xl_block

.drill-data-tile.disabled
  @apply cursor-default bg-gray-100

  div.status
    @apply text-xs mt-3
</style>
